'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface UseCase {
  id: string;
  tabLabel: string;
  persona: string;
  scenario: string;
  before: string;
  after: string;
  metric: string;
  image: string;
  imageAlt: string;
}

const useCases: UseCase[] = [
{
  id: 'uc_ceo',
  tabLabel: 'Geschäftsführer',
  persona: 'Geschäftsführer auf Reisen',
  scenario: 'Sie sind auf einer wichtigen Geschäftsreise in Asien und müssen dringend auf Ihr Bankkonto zugreifen.',
  before: 'Ohne 2FA-Killer: SMS-Code kommt nicht an (Roaming-Problem), Zugriff verweigert, Deal gefährdet.',
  after: 'Mit 2FA-Killer: Code erscheint sofort im Dashboard, Login erfolgreich, Deal gesichert.',
  metric: 'Spart durchschnittlich 3 Stunden pro Woche',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dcc3a028-1766555486152.png",
  imageAlt: 'Business executive in suit working on laptop in modern office'
},
{
  id: 'uc_accounting',
  tabLabel: 'Buchhaltung',
  persona: 'Buchhaltung braucht Zugriff',
  scenario: 'Ihr Finanzteam muss Monatsabschluss machen, aber Sie sind im Urlaub ohne Handy.',
  before: 'Ohne 2FA-Killer: Team wartet auf SMS-Code, Prozesse verzögert, Fristen in Gefahr.',
  after: 'Mit 2FA-Killer: Team hat direkten Zugriff auf Codes, Arbeit läuft weiter, keine Verzögerung.',
  metric: 'Reduziert Wartezeiten um 95%',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1081cafab-1767742933699.png",
  imageAlt: 'Accounting team working together on financial reports with computers'
},
{
  id: 'uc_team',
  tabLabel: 'Team-Verwaltung',
  persona: 'Team-Zugriff verwalten',
  scenario: 'Sie möchten verschiedenen Mitarbeitern Zugriff auf verschiedene Accounts geben.',
  before: 'Ohne 2FA-Killer: Passwörter teilen (unsicher), keine Kontrolle, kein Audit-Trail.',
  after: 'Mit 2FA-Killer: Granulare Berechtigungen, vollständige Kontrolle, lückenlose Audit-Protokolle.',
  metric: 'Verbessert Compliance um 100%',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1efc8391b-1767700962291.png",
  imageAlt: 'Business team collaborating in conference room with laptops and documents'
}];


export default function UseCasesSection() {
  const [activeTab, setActiveTab] = useState(useCases[0].id);
  const activeCase = useCases.find((uc) => uc.id === activeTab) || useCases[0];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-12 reveal">
          <div className="flex justify-center mb-4">
            <div className="bg-blue-100 p-3 rounded-full">
              <Icon name="BriefcaseIcon" size={24} variant="outline" className="text-primary" />
            </div>
          </div>
          <h2 className="font-serif text-3xl md:text-4xl text-foreground tracking-tight mb-4">
            Perfekt für Ihren Einsatz
          </h2>
          <p className="text-muted-foreground font-light">
            Reale Szenarien, in denen 2FA-Killer den Unterschied macht
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-4 mb-12 border-b border-slate-200 pb-4 overflow-x-auto reveal delay-100">
          {useCases.map((useCase) =>
          <button
            key={useCase.id}
            onClick={() => setActiveTab(useCase.id)}
            className={`text-sm font-medium px-6 py-3 rounded-full transition-all whitespace-nowrap ${
            activeTab === useCase.id ?
            'bg-primary text-primary-foreground' :
            'text-muted-foreground hover:text-foreground hover:bg-slate-100'}`
            }>
            
              {useCase.tabLabel}
            </button>
          )}
        </div>

        {/* Active Use Case Content */}
        <div className="grid md:grid-cols-2 gap-12 items-center reveal delay-200">
          {/* Left: Image */}
          <div className="relative rounded-2xl overflow-hidden shadow-xl shadow-slate-900/5">
            <AppImage
              src={activeCase.image}
              alt={activeCase.imageAlt}
              className="w-full h-[400px] object-cover" />
            
            {/* Metric Badge */}
            <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm p-4 rounded-xl shadow-lg">
              <div className="flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-full">
                  <Icon name="ChartBarIcon" size={20} variant="solid" className="text-emerald-600" />
                </div>
                <p className="text-sm font-semibold text-foreground">{activeCase.metric}</p>
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div className="space-y-6">
            <h3 className="font-serif text-2xl text-foreground">{activeCase.persona}</h3>
            <p className="text-muted-foreground leading-relaxed">{activeCase.scenario}</p>

            {/* Before/After Comparison */}
            <div className="space-y-4">
              {/* Before */}
              <div className="p-4 rounded-xl bg-red-50 border border-red-100">
                <div className="flex items-start gap-3">
                  <Icon name="XCircleIcon" size={20} variant="solid" className="text-destructive mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-destructive uppercase tracking-wide mb-1">Vorher</p>
                    <p className="text-sm text-foreground">{activeCase.before}</p>
                  </div>
                </div>
              </div>

              {/* After */}
              <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                <div className="flex items-start gap-3">
                  <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-success uppercase tracking-wide mb-1">Nachher</p>
                    <p className="text-sm text-foreground">{activeCase.after}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}